import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

/**
 * A file chosen for an upload that is sent later, by a press of its own — a ranking's scored result
 * and a firm's returned finalist file, each checked first (SR-454).
 *
 * **A button, never the browser's bare file input**: the native control draws as the text "Choose
 * File no file selected", which a reader did not take for something to press (the client's words of
 * 22 Oct). The kit's outline button opens a hidden input, and the chosen file's name sits beside it.
 * `PhotoInput` is the same press for an upload sent at once; this one only hands the file up.
 *
 * The input keeps `label` as its accessible name, so a spec still finds it by its label and sets
 * its files. Its value is cleared after each choice, so the same file picked again — after an edit
 * on disk — is a change too. Nothing here judges the file: `accept` narrows the picker, and the
 * server says what it refuses.
 */
export function FileChoice({
  label,
  accept,
  file,
  onChoose
}: {
  /** What the file is — "Scored result (CSV or XLSX, at most 10 MB)". */
  label: string;
  accept: string;
  file: Nullable<File>;
  onChoose: (file: File) => void;
}) {
  const input = React.useRef<HTMLInputElement>(null);
  const labelId = React.useId();

  return (
    <div className="grid justify-items-start gap-1 text-sm">
      <span id={labelId} className="text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-describedby={labelId}
          onClick={() => input.current?.click()}
        >
          {file ? 'Choose another file…' : 'Choose file…'}
        </Button>
        {file ? (
          <strong data-chosen-file>{file.name}</strong>
        ) : (
          <span className="text-muted-foreground">No file chosen</span>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept={accept}
        hidden
        tabIndex={-1}
        aria-labelledby={labelId}
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          event.target.value = '';
          // A picker closed with nothing chosen keeps the file already chosen.
          if (chosen) onChoose(chosen);
        }}
      />
    </div>
  );
}
